import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { City } from "@/apis/city/city-apis";
import type { CityWhitelabelMapping, Whitelabel } from "@/types/reducer-states";

interface CitiesState {
  savedWhitelabel: number | null;
  savedWhitelabelFullInfo: Whitelabel | null;
  whiteLabels: Whitelabel[];
  whiteLabelList: Whitelabel[];
  citiesList: City[];
  savedCity: string | null;
  savedCityFullInfo: City | null;
  cityWhitelabelMapping: CityWhitelabelMapping[] | null;

  setSavedWhitelabel: (
    whitelabelId: number | null,
    whitelabelInfo?: Whitelabel | null
  ) => void;
  setWhiteLabels: (whiteLabels: Whitelabel[]) => void;
  setWhiteLabelList: (whiteLabelList: Whitelabel[]) => void;
  setCitiesList: (cities: City[]) => void;
  setSavedCity: (cityCode: string | null, cityInfo?: City | null) => void;
  setCityWhitelabelMapping: (mapping: CityWhitelabelMapping[] | null) => void;
  clearCities: () => void;
}

export const useCitiesStore = create<CitiesState>()(
  persist(
    (set) => ({
      savedWhitelabel: null,
      savedWhitelabelFullInfo: null,
      whiteLabels: [],
      whiteLabelList: [],
      citiesList: [],
      savedCity: null,
      savedCityFullInfo: null,
      cityWhitelabelMapping: null,

      setSavedWhitelabel: (whitelabelId, whitelabelInfo = null) =>
        set({
          savedWhitelabel: whitelabelId,
          savedWhitelabelFullInfo: whitelabelInfo,
        }),

      setWhiteLabels: (whiteLabels) => set({ whiteLabels }),

      setWhiteLabelList: (whiteLabelList) => set({ whiteLabelList }),

      setCitiesList: (cities) => set({ citiesList: cities }),

      setSavedCity: (cityCode, cityInfo = null) =>
        set({
          savedCity: cityCode,
          savedCityFullInfo: cityInfo,
        }),

      setCityWhitelabelMapping: (mapping) =>
        set({ cityWhitelabelMapping: mapping }),

      clearCities: () =>
        set({
          savedWhitelabel: null,
          savedWhitelabelFullInfo: null,
          whiteLabels: [],
          whiteLabelList: [],
          citiesList: [],
          savedCity: null,
          savedCityFullInfo: null,
          cityWhitelabelMapping: null,
        }),
    }),
    {
      name: "cities-store",
      partialize: (state) => ({
        // Only persist the selected city and whitelabel
        savedWhitelabel: state.savedWhitelabel,
        savedCity: state.savedCity,
      }),
    }
  )
);

export const useSavedWhitelabel = () =>
  useCitiesStore((state) => state.savedWhitelabel);
export const useWhiteLabels = () =>
  useCitiesStore((state) => state.whiteLabels);
export const useCitiesList = () => useCitiesStore((state) => state.citiesList);
export const useSavedCity = () => useCitiesStore((state) => state.savedCity);

export const useSavedWhitelabelFullInfo = () =>
  useCitiesStore((state) => state.savedWhitelabelFullInfo);
export const useSavedCityFullInfo = () =>
  useCitiesStore((state) => state.savedCityFullInfo);

export const useWhiteLabelList = () =>
  useCitiesStore((state) => state.whiteLabelList);
export const useCityWhitelabelMapping = () =>
  useCitiesStore((state) => state.cityWhitelabelMapping);
